import { useState } from 'react'
import Modal from '../components/Modal'
import { ErrorText, Field, Row, Text, TimeZoneSelect } from '../components/Form'
import { createHotel, updateHotel, deleteHotel } from '../lib/mutations'
import type { HotelDraft } from '../lib/mutations'
import { browserTimeZone, toDateInputValue, toTimeInputValue } from '../lib/zonedTime'
import type { Hotel, WithId } from '../types/models'

/* ------------------------------------------------------------------ hotels */

export function HotelDialog({
  open,
  hotel,
  tourId,
  ownerId,
  onClose,
}: {
  open: boolean
  hotel?: WithId<Hotel> | null
  tourId: string
  ownerId: string
  onClose: () => void
}) {
  const [draft, setDraft] = useState<HotelDraft>(() => initialHotel(hotel))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const patch = (next: Partial<HotelDraft>) => setDraft((previous) => ({ ...previous, ...next }))
  const valid =
    draft.name.trim().length > 0 &&
    draft.city.trim().length > 0 &&
    draft.checkInDate.length > 0 &&
    draft.checkOutDate.length > 0

  async function save() {
    if (!valid) return
    setBusy(true)
    setError(null)
    try {
      if (hotel) await updateHotel(hotel.id, draft, tourId, hotel.ownerId || ownerId)
      else await createHotel(draft, tourId, ownerId)
      onClose()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      title={hotel ? 'Edit hotel' : 'Add hotel'}
      width={560}
      onClose={onClose}
      footer={
        <>
          {hotel && (
            <button
              className="btn btn-danger"
              style={{ marginRight: 'auto' }}
              disabled={busy}
              onClick={() => setConfirming(true)}
            >
              Delete
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={!valid || busy}>
            {busy ? 'Saving…' : 'Save hotel'}
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Hotel name">
          <Text value={draft.name} onChange={(value) => patch({ name: value })} autoFocus />
        </Field>
        <Field label="Address">
          <Text value={draft.address} onChange={(value) => patch({ address: value })} />
        </Field>
        <Row columns={3}>
          <Field label="City">
            <Text value={draft.city} onChange={(value) => patch({ city: value })} />
          </Field>
          <Field label="Country">
            <Text value={draft.country} onChange={(value) => patch({ country: value })} />
          </Field>
          <Field label="Timezone">
            <TimeZoneSelect value={draft.timezone} onChange={(value) => patch({ timezone: value })} />
          </Field>
        </Row>

        <Row>
          <Field label="Check in">
            <Text
              type="date"
              value={draft.checkInDate}
              onChange={(value) => patch({ checkInDate: value })}
            />
          </Field>
          <Field label="Time">
            <Text
              type="time"
              value={draft.checkInTime}
              onChange={(value) => patch({ checkInTime: value })}
            />
          </Field>
        </Row>
        <Row>
          <Field label="Check out">
            <Text
              type="date"
              value={draft.checkOutDate}
              onChange={(value) => patch({ checkOutDate: value })}
            />
          </Field>
          <Field label="Time">
            <Text
              type="time"
              value={draft.checkOutTime}
              onChange={(value) => patch({ checkOutTime: value })}
            />
          </Field>
        </Row>

        <Field label="Booking reference">
          <Text
            value={draft.bookingReference ?? ''}
            onChange={(value) => patch({ bookingReference: value })}
          />
        </Field>

        <ErrorText>{error}</ErrorText>

        {confirming && hotel && (
          <ConfirmDelete
            label={hotel.name}
            busy={busy}
            onCancel={() => setConfirming(false)}
            onConfirm={async () => {
              setBusy(true)
              try {
                await deleteHotel(hotel.id)
                onClose()
              } catch (caught) {
                setError((caught as Error).message)
              } finally {
                setBusy(false)
              }
            }}
          />
        )}
      </div>
    </Modal>
  )
}

function initialHotel(hotel?: WithId<Hotel> | null): HotelDraft {
  const zone = browserTimeZone()
  if (!hotel) {
    const today = toDateInputValue(new Date())
    return {
      name: '',
      address: '',
      city: '',
      country: '',
      timezone: zone,
      checkInDate: today,
      checkInTime: '15:00',
      checkOutDate: today,
      checkOutTime: '11:00',
      bookingReference: '',
    }
  }
  return {
    name: hotel.name,
    address: hotel.address,
    city: hotel.city,
    country: hotel.country,
    timezone: hotel.timezone,
    checkInDate: toDateInputValue(hotel.checkInDate.toDate(), hotel.timezone),
    checkInTime: toTimeInputValue(hotel.checkInDate.toDate(), hotel.timezone),
    checkOutDate: toDateInputValue(hotel.checkOutDate.toDate(), hotel.timezone),
    checkOutTime: toTimeInputValue(hotel.checkOutDate.toDate(), hotel.timezone),
    bookingReference: hotel.bookingReference ?? '',
  }
}

/* ------------------------------------------------------------------ shared */

export function ConfirmDelete({
  label,
  busy,
  onConfirm,
  onCancel,
}: {
  label: string
  busy: boolean
  onConfirm: () => void | Promise<void>
  onCancel: () => void
}) {
  return (
    <div
      className="stack"
      style={{
        gap: 8,
        padding: 14,
        borderRadius: 'var(--radius-control)',
        background: 'color-mix(in srgb, var(--destructive) 10%, transparent)',
      }}
    >
      <strong>Delete {label}?</strong>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-danger" disabled={busy} onClick={() => void onConfirm()}>
          {busy ? 'Deleting…' : 'Delete'}
        </button>
        <button className="btn btn-quiet" onClick={onCancel}>
          Keep it
        </button>
      </div>
    </div>
  )
}
